/**
 * Slack integration shared types (API + web).
 *
 * The Slack surface is org-scoped: one Slack team per organization
 * (DB-enforced). Per-feature behavior is controlled by admin-managed
 * workspace settings (stored in SlackWorkspace.settings JSON) with
 * default-on semantics (`!== false`).
 */

/** Admin-managed per-workspace feature toggles. Absent key = enabled. */
export interface SlackWorkspaceSettings {
  /** Deliver Scrambl notifications as Slack DMs (per-user opt-in still applies) */
  notificationsEnabled?: boolean
  /** Answer @Scrambl mentions and bot DMs with the AI assistant */
  bennyEnabled?: boolean
  /** Unfurl Scrambl links shared in Slack */
  unfurlsEnabled?: boolean
}

export function isSlackWorkspaceFeatureEnabled(
  settings: Record<string, unknown> | null | undefined,
  key: keyof SlackWorkspaceSettings,
): boolean {
  if (!settings || typeof settings !== 'object') {
    return true
  }

  return settings[key] !== false
}

/**
 * The full bot-scope set the current app version needs. Workspaces installed
 * before a scope was added must re-run OAuth ("Update permissions") — compare
 * granted `SlackWorkspace.scopes` against this list.
 */
export const SLACK_REQUIRED_BOT_SCOPES = [
  'commands',
  'chat:write',
  'chat:write.public',
  'channels:read',
  'im:write',
  'im:history',
  'app_mentions:read',
  'links:read',
  'links:write',
  'users:read',
  'team:read',
] as const

export function getMissingSlackBotScopes(granted: string[]): string[] {
  const grantedSet = new Set(granted)
  return SLACK_REQUIRED_BOT_SCOPES.filter(scope => !grantedSet.has(scope))
}

/** Domain events that can be routed to shared Slack channels. */
export const SlackChannelEventType = {
  TICKET_CREATED: 'TICKET_CREATED',
  TICKET_STATUS_CHANGED: 'TICKET_STATUS_CHANGED',
  TASK_COMPLETED: 'TASK_COMPLETED',
  TASK_STATUS_CHANGED: 'TASK_STATUS_CHANGED',
  DEPLOYMENT_CREATED: 'DEPLOYMENT_CREATED',
  DEPLOYMENT_PROMOTED: 'DEPLOYMENT_PROMOTED',
  SPRINT_STARTED: 'SPRINT_STARTED',
  SPRINT_COMPLETED: 'SPRINT_COMPLETED',
} as const

export type SlackChannelEventType = keyof typeof SlackChannelEventType

export function isSlackChannelEventType(
  value: unknown,
): value is SlackChannelEventType {
  return typeof value === 'string' && value in SlackChannelEventType
}

export const SLACK_CHANNEL_EVENT_TYPE_LABELS: Record<
  SlackChannelEventType,
  string
> = {
  TICKET_CREATED: 'Ticket created',
  TICKET_STATUS_CHANGED: 'Ticket status changed',
  TASK_COMPLETED: 'Task completed',
  TASK_STATUS_CHANGED: 'Task status changed',
  DEPLOYMENT_CREATED: 'Deployment created',
  DEPLOYMENT_PROMOTED: 'Deployment promoted',
  SPRINT_STARTED: 'Sprint started',
  SPRINT_COMPLETED: 'Sprint completed',
}

/** Payload published by domain services into the Slack channel-routing pipeline. */
export interface SlackChannelEvent {
  type: SlackChannelEventType
  organizationId: string
  /** Route scoping: routes with a projectId only match events carrying the same id */
  projectId?: string | null
  actorUserId?: string | null
  entityType: 'TICKET' | 'TASK' | 'DEPLOYMENT' | 'SPRINT'
  entityId: string
  /**
   * Open path WITHOUT the org slug, e.g. `tickets/TCK-42` — the routing
   * layer prefixes `/{orgSlug}/` (domain services don't know the slug).
   */
  openPath: string
  /** Display data consumed by the per-event-type message builders */
  data: Record<string, unknown>
  /** Suppresses duplicate posts when domain hooks double-fire (short TTL) */
  dedupeKey?: string
}

export type SlackChannelRouteStatusType = 'ACTIVE' | 'ERROR' | 'DISABLED'

export interface SlackChannelRouteSummary {
  id: string
  slackChannelId: string
  slackChannelName: string
  projectId: string | null
  projectName: string | null
  eventTypes: SlackChannelEventType[]
  enabled: boolean
  status: SlackChannelRouteStatusType
  lastPostedAt: string | null
  lastErrorAt: string | null
  lastError: string | null
  createdAt: string
}

export interface SlackChannelSummary {
  id: string
  name: string
  isMember: boolean
  numMembers: number | null
}
